"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Database,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  ExternalLink,
  X,
  Settings2,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AgentRagSettingsDialog } from "./rag-settings-dialog";
import type { AgentRagSettings } from "@/lib/agents/shared/types";
import { useKnowledgeBaseFoldersQuery } from "@/hooks/queries/knowledge-base-folders";
import {
  buildAgentKnowledgeBaseDisplayItems,
  documentsOfferedToAgents,
} from "@/lib/ai/knowledge-base/agent-folder-display";
import { KNOWLEDGE_BASE_HREF } from "@/lib/ai/knowledge-base/page-link";
import { KnowledgeBaseFolderRow } from "./knowledge-base-folder-row";

interface KBDocument {
  id: string;
  title: string;
  category?: string;
  token_count?: number;
  is_active: boolean;
  folder_id?: string | null;
  source_type?: string | null;
  /** Computed by API: 'rag' or 'direct' */
  mode?: "rag" | "direct";
}

interface AgentKnowledgeBaseSettingsProps {
  /** Agent ID (reserved for future per-agent API calls) */
  agentId?: string;
  /** Currently selected KB document IDs */
  selectedKnowledgeBases: string[];
  /** Current RAG settings */
  ragSettings?: AgentRagSettings;
  /** Save selected KB IDs to agent config */
  onSaveKnowledgeBases: (kbIds: string[]) => Promise<boolean>;
  /** Save RAG settings to agent config */
  onSaveAgentRagSettings: (settings: AgentRagSettings) => Promise<boolean>;
  /** Whether the user has manage permissions */
  canManage?: boolean;
}

function getDocMode(doc: KBDocument): "rag" | "direct" {
  return doc.mode || "rag";
}

export function AgentKnowledgeBaseSettings({
  selectedKnowledgeBases,
  ragSettings,
  onSaveKnowledgeBases,
  onSaveAgentRagSettings,
  canManage = true,
}: AgentKnowledgeBaseSettingsProps) {
  const { fetchWithAuth, user } = useAuth();
  const foldersQuery = useKnowledgeBaseFoldersQuery();

  const [kbDocuments, setKbDocuments] = useState<KBDocument[]>([]);
  const [allKbDocuments, setAllKbDocuments] = useState<KBDocument[]>([]);
  const [isLoadingKB, setIsLoadingKB] = useState(true);
  /** True after a successful API response. Used to gate stale-ref cleanup so a
   *  failed network request can't be misread as "all docs are stale". */
  const [loadSuccess, setLoadSuccess] = useState(false);
  /** Set of ALL existing KB document IDs (active + inactive). Used only for the
   *  stale-ref cleanup check, so we don't unlink agents from temporarily
   *  deactivated docs. Rendered toggles still use `kbDocuments` (active only). */
  const [allKnownKbIds, setAllKnownKbIds] = useState<Set<string>>(new Set());
  const [togglingKBId, setTogglingKBId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const [ragDialogOpen, setRagDialogOpen] = useState(false);
  /** Guard to prevent stale-ref cleanup from running more than once per load */
  const staleCleanupAttempted = useRef(false);

  const loadKBDocuments = useCallback(async () => {
    setIsLoadingKB(true);
    setLoadSuccess(false);
    staleCleanupAttempted.current = false;
    try {
      // Single unfiltered request — split client-side:
      // - kbDocuments (active only) drives the rendered toggles
      // - allKnownKbIds (active + inactive) gates stale-ref cleanup so we
      //   preserve links to temporarily deactivated docs
      const response = await fetchWithAuth(
        "/api/ai/knowledge-base/documents"
      );
      if (response.ok) {
        const data = await response.json();
        const allDocs: KBDocument[] = documentsOfferedToAgents(data.documents || []);
        setAllKbDocuments(allDocs);
        setKbDocuments(allDocs.filter((d) => d.is_active));
        setAllKnownKbIds(new Set(allDocs.map((d) => d.id)));
        setLoadSuccess(true);
      }
    } catch (err) {
      console.error("Error loading KB documents:", err);
    } finally {
      setIsLoadingKB(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadKBDocuments();
  }, [loadKBDocuments]);

  /**
   * Auto-clean truly stale references: if the agent config has KB IDs that no
   * longer exist (deleted from DB), prune them. Deactivated docs are preserved
   * via `allKnownKbIds` so a temporary deactivation doesn't lose the link.
   *
   * Note: the KB DELETE endpoint already cleans up agent configs on delete.
   * This effect handles legacy orphans + any case the cleanup missed
   * (race conditions, manual DB ops, bulk deletes).
   *
   * Guarded by `loadSuccess` so a failed API call can't wipe the agent's KB
   * list, and by `staleCleanupAttempted` to prevent loops.
   */
  useEffect(() => {
    if (!loadSuccess) return;
    if (staleCleanupAttempted.current) return;
    if (selectedKnowledgeBases.length === 0) return;

    const staleIds = selectedKnowledgeBases.filter((id) => !allKnownKbIds.has(id));
    if (staleIds.length === 0) return;

    staleCleanupAttempted.current = true;
    const cleaned = selectedKnowledgeBases.filter((id) => allKnownKbIds.has(id));
    console.warn(
      `[KnowledgeBase] Removing ${staleIds.length} stale KB reference(s) from agent config:`,
      staleIds,
    );
    onSaveKnowledgeBases(cleaned)
      .then((ok) => {
        if (ok) {
          setMessage({
            type: "success",
            text: `Removed ${staleIds.length} unavailable knowledge base reference${staleIds.length === 1 ? "" : "s"} that no longer exist`,
          });
        }
      })
      .catch((err) => {
        console.error("[KnowledgeBase] Failed to clean stale KB references:", err);
      });
  }, [loadSuccess, allKnownKbIds, selectedKnowledgeBases, onSaveKnowledgeBases]);

  const handleToggleKB = async (
    kbIds: string[],
    itemKey: string,
    isFolder = false,
  ) => {
    const selectedIds = new Set(selectedKnowledgeBases);
    const isRemoving = kbIds.every(kbId => selectedIds.has(kbId));
    const toggledIds = new Set(kbIds);
    const newKBs = isRemoving
      ? selectedKnowledgeBases.filter(id => !toggledIds.has(id))
      : [...new Set([...selectedKnowledgeBases, ...kbIds])];

    setTogglingKBId(itemKey);
    setMessage(null);

    try {
      const saved = await onSaveKnowledgeBases(newKBs);
      if (saved) {
        const doc = kbDocuments.find(kb => kbIds.includes(kb.id));
        const mode = doc ? getDocMode(doc) : "rag";
        setMessage({
          type: "success",
          text: isRemoving
            ? `${isFolder ? "Knowledge base folder" : "Knowledge base"} removed from agent`
            : `${isFolder ? "Knowledge base folder" : "Knowledge base"} added (${mode === "rag" ? "RAG" : "Direct"} mode)`,
        });
      } else {
        setMessage({ type: "error", text: "Failed to save changes" });
      }
    } catch (error) {
      console.error("Error toggling KB:", error);
      setMessage({ type: "error", text: "Failed to save changes" });
    } finally {
      setTogglingKBId(null);
    }
  };

  // Auto-hide success messages, keep errors visible
  useEffect(() => {
    if (!message) return;

    if (message.type === "error") {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Check if any selected KB uses RAG mode
  const hasRagDocuments = kbDocuments.some(
    (kb) => selectedKnowledgeBases.includes(kb.id) && getDocMode(kb) === "rag"
  );

  const displayItems = useMemo(
    () => buildAgentKnowledgeBaseDisplayItems({
      activeDocuments: kbDocuments,
      allDocuments: allKbDocuments,
      folders: foldersQuery.data?.folders ?? [],
      selectedDocumentIds: selectedKnowledgeBases,
    }),
    [allKbDocuments, foldersQuery.data?.folders, kbDocuments, selectedKnowledgeBases],
  );

  if (isLoadingKB || foldersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-muted-foreground">Loading knowledge base...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Knowledge Base</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Connect documents to provide context-aware AI responses.
        </p>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-lg">Documents</CardTitle>
                  {selectedKnowledgeBases.length > 0 && (
                    <Badge
                      variant="outline"
                      className="text-purple-600 border-purple-600"
                    >
                      {selectedKnowledgeBases.length} <span>selected</span>
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  Select knowledge bases for this agent
                </CardDescription>
              </div>
            </div>
            {hasRagDocuments && canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRagDialogOpen(true)}
                className="w-full text-orange-600 border-orange-300 hover:bg-orange-50 sm:w-auto"
              >
                <Settings2 className="w-4 h-4 mr-1.5" />
                <span>RAG Config</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
          {message && (
            <Alert
              ref={alertRef}
              className={
                message.type === "success"
                  ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                  : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
              }
            >
              <div className="flex items-start gap-3">
                {message.type === "success" ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <AlertDescription
                  className={`flex-1 ${
                    message.type === "success"
                      ? "text-green-800 dark:text-green-200"
                      : "text-red-800 dark:text-red-200"
                  }`}
                >
                  {message.text}
                </AlertDescription>
                {message.type === "error" && (
                  <button
                    onClick={() => setMessage(null)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </Alert>
          )}

          {displayItems.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No knowledge base documents available. Create documents in the Knowledge Base section first.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {displayItems.map((item) => {
                if (item.kind === "folder") {
                  const isToggling = togglingKBId === item.key;
                  const isDisabled = togglingKBId !== null || !canManage;

                  return (
                    <KnowledgeBaseFolderRow
                      key={item.key}
                      name={item.folder.name}
                      documentCount={item.folder.document_count}
                      selectedDocumentCount={item.selectedDocumentCount}
                      checked={item.isSelected}
                      isBusy={isToggling}
                      disabled={isDisabled}
                      onToggle={() => handleToggleKB(item.documentIds, item.key, true)}
                    />
                  );
                }

                const kb = item.document;
                const isSelected = selectedKnowledgeBases.includes(kb.id);
                const isToggling = togglingKBId === item.key;
                const isDisabled = togglingKBId !== null || !canManage;
                const mode = getDocMode(kb);

                return (
                  <label
                    key={item.key}
                    className={`flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors sm:items-center sm:p-4 ${
                      isDisabled
                        ? "opacity-60 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      isSelected
                        ? "border-purple-300 bg-purple-50"
                        : "border-gray-200 hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? "bg-purple-200" : "bg-gray-100"
                        }`}
                      >
                        {isToggling ? (
                          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                        ) : (
                          <FileText
                            className={`w-5 h-5 ${
                              isSelected ? "text-purple-600" : "text-gray-500"
                            }`}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="break-words font-medium">{kb.title}</span>
                          <Badge
                            variant="outline"
                            className={
                              mode === "rag"
                                ? "text-orange-600 border-orange-300 bg-orange-50 text-xs"
                                : "text-blue-600 border-blue-300 bg-blue-50 text-xs"
                            }
                          >
                            {mode === "rag" ? "RAG" : "Direct"}
                          </Badge>
                        </div>
                        <div className="break-words text-sm text-muted-foreground">
                          {isToggling ? (
                            selectedKnowledgeBases.includes(kb.id) ? "Removing..." : "Adding..."
                          ) : (
                            <>
                              {kb.category && <span>{kb.category}</span>}
                              {kb.category && kb.token_count && <span> · </span>}
                              {kb.token_count && <span>~{kb.token_count.toLocaleString()} tokens</span>}
                              {!kb.category && !kb.token_count && <span>Document</span>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      className="mt-1 shrink-0 sm:mt-0"
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => handleToggleKB(item.documentIds, item.key)}
                    />
                  </label>
                );
              })}
            </div>
          )}

          {user?.isManager && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                window.open(`/${KNOWLEDGE_BASE_HREF}`, "_blank")
              }
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              <span>Manage Knowledge Base</span>
            </Button>
          )}
        </CardContent>
      </Card>

      <AgentRagSettingsDialog
        open={ragDialogOpen}
        onOpenChange={setRagDialogOpen}
        hasRagDocuments={hasRagDocuments}
        currentSettings={ragSettings}
        onSave={onSaveAgentRagSettings}
      />
    </div>
  );
}
